import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Container, Form, Button, Spinner } from "react-bootstrap";
import ClientSideReactTable from "commons/table/ClientSideReactTable";
import { showNotification } from "services/NotificationService";
import { hasRoleIn } from "rbac/RbacHelper";
import PERMISSIONS from "rbac/Permissions.json";
import { ColumnTypes, FilterTypes } from "commons/table/Constants";
import {
  formatInputMaskDisplay,
  formatInputMaskSubmitValue,
  getInputMaskDigitCapacity,
  isInputMaskComplete,
} from "commons/utils/inputMask.utils";
import {
  buildVerifyApiRequestPayload,
  executeVerifyApiRequest,
  extractVerifyErrorMessage,
  normalizeVerifyTableRecords,
  parseVerifyApiResponse,
} from "./unprocessedFaxVerifyApi.utils";
import "./UnprocessedFaxCustomList.css";

const COLUMN_TYPE_MAP = {
  Text: ColumnTypes.Text,
  Link: ColumnTypes.Link,
  RouteableLink: ColumnTypes.RouteableLink,
  DateColumn: ColumnTypes.DateColumn,
  DateTimeColumn: ColumnTypes.DateTimeColumn,
  Boolean: ColumnTypes.Boolean,
  Custom: ColumnTypes.Custom,
  Money: ColumnTypes.Money,
};

const FILTER_TYPE_MAP = {
  SearchableInput: FilterTypes.SearchableInput,
  SingleSelect: FilterTypes.SingleSelect,
  MultiSelect: FilterTypes.MultiSelect,
  Date: FilterTypes.Date,
  DateRangePicker: FilterTypes.DateRangePicker,
  MultiSelectAll: FilterTypes.MultiSelectAll,
};

const buildTableColumnsFromConfig = (
  columns = [],
  { showFilters = true, enableSorting = true } = {}
) =>
  columns.map((column) => ({
    ...column,
    accessor: column.accessor || column.accessorKey,
    accessorKey: column.accessorKey || column.accessor,
    columnType: COLUMN_TYPE_MAP[column.columnType] || ColumnTypes.Text,
    filterType: column.filterType
      ? FILTER_TYPE_MAP[column.filterType] || FilterTypes.SearchableInput
      : FilterTypes.SearchableInput,
    enableColumnFilter:
      column.enableColumnFilter !== undefined
        ? column.enableColumnFilter
        : showFilters,
    enableSorting:
      column.enableSorting !== undefined
        ? column.enableSorting
        : enableSorting,
    canSort:
      column.enableSorting !== undefined
        ? column.enableSorting
        : enableSorting,
    disableSortBy:
      column.enableSorting !== undefined
        ? !column.enableSorting
        : !enableSorting,
  }));

const resolveVerifyInputValue = (verifyInput, inputValue, maskedDigits) => {
  const maskConfig = verifyInput?.inputMask;
  const label = verifyInput?.label || "Input";

  if (maskConfig?.pattern) {
    const { pattern, emptyChar = "0", submitFormat = "masked" } = maskConfig;
    const requireComplete =
      verifyInput.requireCompleteMask !== undefined
        ? verifyInput.requireCompleteMask
        : verifyInput.required !== false;

    if (verifyInput.required !== false && !maskedDigits) {
      return { error: `${label} is required.` };
    }

    if (requireComplete && !isInputMaskComplete(maskedDigits, pattern)) {
      return {
        error:
          verifyInput.incompleteMaskMessage ||
          `${label} must match the format ${formatInputMaskDisplay("", pattern, emptyChar)}.`,
      };
    }

    return {
      value: formatInputMaskSubmitValue(maskedDigits, pattern, submitFormat),
    };
  }

  const trimmedValue = String(inputValue || "").trim();

  if (verifyInput.required !== false && !trimmedValue) {
    return { error: `${label} is required.` };
  }

  if (verifyInput.minLength && trimmedValue.length < verifyInput.minLength) {
    return {
      error: `${label} must be at least ${verifyInput.minLength} characters.`,
    };
  }

  return { value: trimmedValue };
};

const MaskedVerifyInput = ({
  maskConfig,
  digits,
  onDigitsChange,
  onEnter,
  disabled,
  label,
  dataTestId,
  hasError,
}) => {
  const pattern = maskConfig.pattern;
  const emptyChar = maskConfig.emptyChar ?? "0";
  const displayValue = formatInputMaskDisplay(digits, pattern, emptyChar);
  const capacity = getInputMaskDigitCapacity(pattern);

  const appendDigits = (rawValue) => {
    const nextDigits = `${digits}${String(rawValue).replace(/\D/g, "")}`.slice(
      0,
      capacity
    );
    onDigitsChange(nextDigits);
  };

  const handleKeyDown = (event) => {
    if (event.key >= "0" && event.key <= "9") {
      event.preventDefault();
      if (digits.length < capacity) {
        onDigitsChange(`${digits}${event.key}`);
      }
      return;
    }

    if (event.key === "Backspace") {
      event.preventDefault();
      onDigitsChange(digits.slice(0, -1));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      onEnter();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    appendDigits(event.clipboardData.getData("text"));
  };

  return (
    <Form.Control
      type="text"
      inputMode="numeric"
      autoComplete="off"
      className="unprocessed-fax-custom-list__masked-input"
      value={displayValue}
      readOnly
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      disabled={disabled}
      data-testid={dataTestId}
      aria-label={label || "Verify input"}
      aria-invalid={hasError}
    />
  );
};

const VERIFY_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
};

const UnprocessedFaxCustomList = ({
  config,
  faxInfoId,
  setSelectedRows = () => {},
  values,
  setValues,
  resetTableSelection = 0,
  dataTestId = "unprocessed-fax-custom-list",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [maskedDigits, setMaskedDigits] = useState("");
  const [records, setRecords] = useState([]);
  const [verifyStatus, setVerifyStatus] = useState(VERIFY_STATUS.IDLE);
  const [verifyError, setVerifyError] = useState("");
  const [hasVerified, setHasVerified] = useState(false);
  const [tableSelectionKey, setTableSelectionKey] = useState(0);

  const tableConfig = config?.table || {};
  const verifyInput = config?.verifyInput || {};
  const verifyButton = config?.verifyButton || {};
  const verifyApi = config?.verifyApi || {};
  const selectionConfig = config?.selection || {};

  const isSingleSelect = selectionConfig.singleSelect === true;
  const enableMultiRowSelection = isSingleSelect
    ? false
    : selectionConfig.enableMultiRowSelection !== false;

  const rowIdKey = tableConfig.rowIdKey || selectionConfig.rowIdKey || "id";
  const showFilters = tableConfig.showFilters !== false;
  const enableSorting = tableConfig.enableSorting !== false;

  const inputMaskConfig = verifyInput.inputMask;

  const showPagination = tableConfig.showPagination === true;

  const columns = useMemo(
    () =>
      buildTableColumnsFromConfig(tableConfig.columns || [], {
        showFilters,
        enableSorting,
      }),
    [tableConfig.columns, showFilters, enableSorting]
  );

  const tablePageSize = showPagination
    ? tableConfig.defaultPageSize || 5
    : Math.max(records.length, 1);

  const clearTableCheckboxSelection = useCallback(() => {
    setTableSelectionKey((key) => key + 1);
  }, []);

  const clearTableSelection = useCallback(() => {
    setTableSelectionKey((key) => key + 1);
    setSelectedRows([], values, setValues, [], []);
  }, [setSelectedRows, values, setValues]);

  const resetListState = useCallback(() => {
    setInputValue("");
    setMaskedDigits("");
    setRecords([]);
    setVerifyStatus(VERIFY_STATUS.IDLE);
    setVerifyError("");
    setHasVerified(false);
    setTableSelectionKey(0);
  }, []);

  useEffect(() => {
    if (resetTableSelection > 0) {
      resetListState();
    }
  }, [resetTableSelection, resetListState]);

  useEffect(() => {
    return () => resetListState();
  }, [resetListState]);

  const handleVerify = () => {
    const { value: verifyValue, error: inputError } = resolveVerifyInputValue(
      verifyInput,
      inputValue,
      maskedDigits
    );

    if (inputError) {
      setVerifyError(inputError);
      setVerifyStatus(VERIFY_STATUS.ERROR);
      return;
    }

    if (!verifyApi?.url) {
      setVerifyError("Verify API URL is not configured.");
      setVerifyStatus(VERIFY_STATUS.ERROR);
      return;
    }

    if (isSingleSelect) {
      clearTableSelection();
    } else if (selectionConfig.clearSelectionOnVerify) {
      if (selectionConfig.preserveLinkPagesOnVerify) {
        clearTableCheckboxSelection();
      } else {
        clearTableSelection();
      }
    }

    setVerifyStatus(VERIFY_STATUS.LOADING);
    setVerifyError("");
    setHasVerified(true);

    const onVerifySuccess = (normalized) => {
      setRecords(normalized);
      setVerifyStatus(VERIFY_STATUS.SUCCESS);

      if (normalized.length === 0) {
        showNotification({
          title: config.emptyTitle || "No results",
          message:
            tableConfig.emptyMessage || "No records matched your search.",
          type: "info",
          position: "tr",
        });
      }
    };

    const onVerifyFailed = (message) => {
      setRecords([]);
      setVerifyError(message);
      setVerifyStatus(VERIFY_STATUS.ERROR);
      showNotification({
        title: config.errorTitle || "Verification failed",
        message,
        type: "error",
        position: "tr",
      });
    };

    const requestPayload = buildVerifyApiRequestPayload({
      verifyApi,
      verifyInput,
      faxInfoId,
      inputValue: verifyValue,
    });

    executeVerifyApiRequest(verifyApi, requestPayload)
      .then((responseData) => {
        const { records: resolvedRecords, failures } =
          parseVerifyApiResponse(responseData);

        if (failures.length > 0 && resolvedRecords.length === 0) {
          onVerifyFailed(
            failures[0]?.message ||
              "The provided reference ID could not be resolved."
          );
          return;
        }

        onVerifySuccess(
          normalizeVerifyTableRecords(resolvedRecords, rowIdKey)
        );

        if (failures.length > 0) {
          showNotification({
            title:
              config.partialFailureTitle ||
              "Some references could not be resolved",
            message: failures.map((failure) => failure.message).join(" "),
            type: "warning",
            position: "tr",
          });
        }
      })
      .catch((error) => {
        onVerifyFailed(extractVerifyErrorMessage(error));
      });
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleVerify();
    }
  };

  const onRowSelect = useCallback(
    (selectedRowMap) => {
      const selectedIds = Object.keys(selectedRowMap).filter((id) => selectedRowMap[id]);
      const selectedRecords = records.filter(
        (record) =>
          selectedRowMap[record.id] || selectedRowMap[String(record.id)]
      );
      setSelectedRows(selectedIds, values, setValues, selectedRecords);
    },
    [setSelectedRows, values, setValues, records]
  );

  const isLoading = verifyStatus === VERIFY_STATUS.LOADING;
  const verifyButtonLabel =
    verifyButton.label || "Verify";
  const verifyButtonVariant = verifyButton.variant || "primary";

  if (config?.phiRequired !== false && !hasRoleIn([PERMISSIONS.PERM_PHI_ACCESS])) {
    return (
      <Container className="list-container unprocessed-fax-custom-list" fluid>
        <Card variant="primary" className="mb-3">
          <Card.Title className="text-center">
            <h5>
              {config.phiDeniedMessage ||
                "User does not have PHI access to view this list"}
            </h5>
          </Card.Title>
        </Card>
      </Container>
    );
  }

  return (
    <div
      className="unprocessed-fax-custom-list"
      data-testid={dataTestId}
    >
      <Card className="unprocessed-fax-custom-list__card">
        {(config.title || config.description) && (
          <div className="unprocessed-fax-custom-list__header">
            {config.title && (
              <h5 className="unprocessed-fax-custom-list__title">{config.title}</h5>
            )}
            {config.description && (
              <p className="unprocessed-fax-custom-list__description">
                {config.description}
              </p>
            )}
          </div>
        )}

        <div className="unprocessed-fax-custom-list__verify-panel">
          <div className="unprocessed-fax-custom-list__verify-form">
            <div className="unprocessed-fax-custom-list__input-group">
              <Form.Label className="unprocessed-fax-custom-list__input-label">
                {verifyInput.label || "Search"}
              </Form.Label>
              <div className="unprocessed-fax-custom-list__verify-field-row">
                {inputMaskConfig?.pattern ? (
                  <MaskedVerifyInput
                    maskConfig={inputMaskConfig}
                    digits={maskedDigits}
                    onDigitsChange={(nextDigits) => {
                      setMaskedDigits(nextDigits);
                      if (verifyError) setVerifyError("");
                    }}
                    onEnter={handleVerify}
                    disabled={isLoading}
                    label={verifyInput.label}
                    dataTestId={`${dataTestId}-verify-input`}
                    hasError={Boolean(verifyError)}
                  />
                ) : (
                  <Form.Control
                    type={verifyInput.type || "text"}
                    value={inputValue}
                    placeholder={verifyInput.placeholder || ""}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      if (verifyError) setVerifyError("");
                    }}
                    onKeyDown={handleInputKeyDown}
                    disabled={isLoading}
                    data-testid={`${dataTestId}-verify-input`}
                    aria-label={verifyInput.label || "Verify input"}
                  />
                )}
                <Button
                  variant={verifyButtonVariant}
                  onClick={handleVerify}
                  disabled={isLoading}
                  className="unprocessed-fax-custom-list__verify-btn"
                  data-testid={`${dataTestId}-verify-button`}
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span className="visually-hidden">Verifying</span>
                    </>
                  ) : (
                    verifyButtonLabel
                  )}
                </Button>
              </div>
              {inputMaskConfig?.pattern && (
                <Form.Text className="text-muted unprocessed-fax-custom-list__mask-hint">
                  {verifyInput.maskHelperText || ""}
                </Form.Text>
              )}
            </div>
          </div>

          <div className="unprocessed-fax-custom-list__status">
            {hasVerified && verifyStatus === VERIFY_STATUS.SUCCESS && (
              <span className="unprocessed-fax-custom-list__status-pill unprocessed-fax-custom-list__status-pill--success">
                {records.length} record{records.length === 1 ? "" : "s"} found
              </span>
            )}
            {hasVerified &&
              verifyStatus === VERIFY_STATUS.SUCCESS &&
              records.length === 0 && (
                <span className="unprocessed-fax-custom-list__status-pill unprocessed-fax-custom-list__status-pill--muted">
                  No matching records
                </span>
              )}
            {!hasVerified && (
              <span className="unprocessed-fax-custom-list__status-pill unprocessed-fax-custom-list__status-pill--muted">
                {tableConfig.emptyBeforeVerifyMessage ||
                  "Enter a value and click Verify to load results"}
              </span>
            )}
            {verifyError && (
              <span className="unprocessed-fax-custom-list__status-pill unprocessed-fax-custom-list__status-pill--error">
                {verifyError}
              </span>
            )}
          </div>
        </div>

        <div className="unprocessed-fax-custom-list__table-wrap">
          {hasVerified && records.length > 0 ? (
            <ClientSideReactTable
              key={`${dataTestId}-${resetTableSelection}-${tableSelectionKey}-${records.length}`}
              columnDefination={columns}
              data={records}
              loading={isLoading}
              addRowSelection={selectionConfig.enabled !== false}
              enableMultiRowSelection={enableMultiRowSelection}
              enableAllRowSelect={selectionConfig.enableAllRowSelect === true}
              onRowSelect={onRowSelect}
              overrideRowId={rowIdKey}
              pageSize={tablePageSize}
              pageSizeOptions={tableConfig.pageSizeOptions || [5, 10, 20]}
              showPagination={showPagination}
            />
          ) : (
            <div className="unprocessed-fax-custom-list__empty">
              {isLoading
                ? "Loading results..."
                : hasVerified
                  ? tableConfig.emptyMessage ||
                    "No records found for the provided value."
                  : tableConfig.emptyBeforeVerifyMessage ||
                    "Results will appear here after verification."}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default UnprocessedFaxCustomList;
